import * as api from "app/api/competitionsApi";
import { getFavouriteList } from "app/api/favouritesApi";
import { AsianCompetitionHelper } from "app/helpers/asianCompetitionHelper";
import { CompetitionHelper } from "app/helpers/competitionHelper";
import { EventHelper } from "app/helpers/eventHelper";
import { SessionHelper } from "app/helpers/sessionHelper";
import type {
  CompetitionDataType,
  CompetitionType,
} from "app/types/competitionType";
import type { EventType } from "app/types/eventType";
import type { CompetitionListModelType } from "app/types/rematchTypes";
import {
  competitionListTimeEnum,
  ListRenderValues,
  TabTypes,
} from "app/utils/const";
import cloneDeep from "lodash/cloneDeep";

/**
 * reducers: `myFunc(state, payload)`
 * effects:  `async myFunc(payload, rootState)`
 */
const CompetitionListModel = {
  state: {
    competitionList: [] as Array<any>,
    competitionListTotal: 0,
    competitionListPerPage: 180,
    competitionListOffset: 0,
    hasCompetitionListReachedEnd: false,
    isCompetitionListLoading: false,
    setCompetitionListLoading: false,
    competitionListNumberLiveEvents: 0,
    disableFilterTab: false,
    type: TabTypes.POPULAR,
    asianType: TabTypes.POPULAR,
    marketGroupType: null,
    time: SessionHelper.isAsianRouteAndSessionView()
      ? competitionListTimeEnum.TODAY
      : SessionHelper.getCompetitionListTime() || competitionListTimeEnum.TODAY,
    disableTodayEarlyFilterTab: false,
    bookmarksOnly: false,
    favouritesOnly: false,
    futures: false,
    singleCompetitionOnly: false,
    sportId: null,
    marketType: null,
    line: "MAIN",
    asianPreGameEventsOnly: false,
    showUnsummarizedMarketsModal: false,
    unsummarizedMarketsModalEventId: -1,
    unsummarizedMarketsModalEvent: [] as Array<any>,
    unsummarizedMarketsModalEventLoading: false,
    searchResultModalActive: false,
    compIds: [] as Array<any>,
    pregameOptions: [] as Array<any>,
    sortingOptions: [] as Array<any>,
    lineOptions: [] as Array<any>,
    oddsOptions: [] as Array<any>,
    fullListLoaded: false,
    searchResult: false,
  },
  //
  reducers: {
    /**
     * payload: { competitionList }
     */
    updateCompetitionList: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, competitionList: [...payload.competitionList] };
    },
    // Reset loading flags when exiting favourites-only view
    // Resolves a possible issue inherited from previous competition list logic
    resetLoadingOnCloseFavourites: (state: Record<string, any>): any => {
      return {
        ...state,
        isCompetitionListLoading: false,
        setCompetitionListLoading: false,
      };
    },
    /**
     * payload: { disableFilterTab:boolean }
     */
    doNotAppendCompetitionList: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, hasCompetitionListReachedEnd: payload };
    },

    setFullListLoaded: (state: Record<string, any>): any => {
      return { ...state, fullListLoaded: true };
    },

    setMarketGroupType: (state: Record<string, any>, payload: boolean): any => {
      return { ...state, marketGroupType: payload };
    },
    //
    setUnsummarizedMarketsModalToOpen: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return {
        ...state,
        showUnsummarizedMarketsModal: true,
        unsummarizedMarketsModalEventId: payload.id,
        unsummarizedMarketsModalEvent: payload,
      };
    },
    //
    updateUnsummarizedMarketsModalEvent: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, unsummarizedMarketsModalEvent: payload };
    },
    //
    unsetUnsummarizedMarketsModalToOpen: (state: Record<string, any>): any => {
      return {
        ...state,
        showUnsummarizedMarketsModal: false,
        unsummarizedMarketsModalEventId: -1,
      };
    },
    updateUnsummarizedMarketsModalEventLoading: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, unsummarizedMarketsModalEventLoading: payload };
    },
    setSearchResultModalActive: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, searchResultModalActive: payload };
    },

    /**
     * payload: { competitionListNumberLiveEvents:number }
     */
    updateCompetitionListNumberLiveEvents: (
      state: Record<string, any>,
      payload: number
    ): any => {
      return { ...state, competitionListNumberLiveEvents: payload };
    },

    /**
     * payload: { disableFilterTab:boolean }
     */
    disableFilter: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, disableFilterTab: payload };
    },

    /**
     * payload: { disableFilterTab:boolean }
     */
    disableTodayEarlyFilter: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, disableTodayEarlyFilterTab: payload };
    },

    /**
     * payload: { favouritesOnly:boolean }
     */
    setFavouritesOnly: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, favouritesOnly: payload };
    },

    /**
     * payload: { favouritesOnly:boolean }
     */
    setFutures: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, futures: payload };
    },

    /**
     * payload: { pregameOptions:Object }
     */
    setPregameOptions: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, pregameOptions: payload.pregameOptions };
    },

    /**
     * payload: { oddsOptions:Object }
     */
    setOddsOptions: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, oddsOptions: payload.oddsOptions };
    },

    /**
     * payload: { sortingOptions:Object }
     */
    setSortingOptions: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, sortingOptions: payload.sortingOptions };
    },

    /**
     * payload: { lineOptions:Object }
     */
    setLineOptions: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, lineOptions: payload.lineOptions };
    },

    /**
     * payload: { singleCompetitionOnly:boolean }
     */
    setSingleCompetitionOnly: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, singleCompetitionOnly: payload };
    },

    /**
     * payload: number
     */
    setSportId(state: Record<string, any>, payload: Record<string, any>): any {
      return { ...state, sportId: payload.sportId };
    },

    /**
     * reset competitionlist on filter modal load: { competitionList }
     * BET-2527
     */
    resetCompetition(state: Record<string, any>): any {
      return {
        ...state,
        competitionList: [],
        competitionListTotal: 0,
        competitionListOffset: 0,
        hasCompetitionListReachedEnd: true,
      };
    },

    /**
     * payload: CompetitionType
     */
    updateCompetitionInList(
      state: CompetitionListModelType,
      payload: CompetitionType
    ): {
      competitionList: any;
      competitionListOffset: number;
      competitionListPerPage: number;
      hasCompetitionListReachedEnd: boolean;
      isCompetitionListLoading: boolean;
      time: string;
      unsummarizedMarketsModalEvent: EventType;
      unsummarizedMarketsModalEventId: number;
    } {
      let competitionList = cloneDeep(state.competitionList);
      const competitionIndex = competitionList.findIndex(
        competition =>
          competition.id === payload.id && competition.live === payload.live
      );

      if (competitionIndex !== -1) {
        // merge the new changes
        const newCompetition = Object.assign(
          competitionList[competitionIndex],
          payload
        );
        competitionList[competitionIndex] = newCompetition;
      }

      return { ...state, competitionList };
    },

    //
    addBookmarkId(
      state: CompetitionListModelType,
      bookmarkId: number,
      competitionId: number
    ): {
      competitionList: any;
      competitionListOffset: number;
      competitionListPerPage: number;
      hasCompetitionListReachedEnd: boolean;
      isCompetitionListLoading: boolean;
      time: string;
      unsummarizedMarketsModalEvent: EventType;
      unsummarizedMarketsModalEventId: number;
    } {
      let competitionList = cloneDeep(state.competitionList);
      const competitionIndex = competitionList.findIndex(
        competition => competition.id === competitionId
      );

      if (competitionIndex !== -1) {
        const newCompetition = Object.assign(competitionList[competitionIndex]);
        // add bookmark id to new competions object
        newCompetition.favoriteId = bookmarkId;
        //update competition list with new competitions object
        competitionList[competitionIndex] = newCompetition;
      }

      return { ...state, competitionList };
    },

    //
    removeBookmarkId(
      state: CompetitionListModelType,
      bookmarkId: number,
      competitionId: number
    ): {
      competitionList: any;
      competitionListOffset: number;
      competitionListPerPage: number;
      hasCompetitionListReachedEnd: boolean;
      isCompetitionListLoading: boolean;
      time: string;
      unsummarizedMarketsModalEvent: EventType;
      unsummarizedMarketsModalEventId: number;
    } {
      let competitionList = cloneDeep(state.competitionList);
      const competitionIndex = competitionList.findIndex(
        competition => competition.id === competitionId
      );

      if (competitionIndex !== -1) {
        const newCompetition = Object.assign(competitionList[competitionIndex]);
        // remove bookmarkId from new competition object
        delete newCompetition.favoriteId;
        // update competitonList with new competition
        competitionList[competitionIndex] = newCompetition;
      }

      return { ...state, competitionList };
    },

    /**
     * payload: EventType
     */
    updateEventInAsianCompetitionInList(
      state: CompetitionListModelType,
      payload: EventType
    ): {
      competitionList: any;
      competitionListOffset: number;
      competitionListPerPage: number;
      hasCompetitionListReachedEnd: boolean;
      isCompetitionListLoading: boolean;
      time: string;
      unsummarizedMarketsModalEvent: any;
      unsummarizedMarketsModalEventId: number;
      unsummarizedMarketsModalEventLoading: boolean;
    } {
      let competitionList = cloneDeep(state.competitionList);
      let unsummarizedMarketsModalEventId = cloneDeep(
        state.unsummarizedMarketsModalEventId
      );
      let unsummarizedMarketsModalEvent = cloneDeep(
        state.unsummarizedMarketsModalEvent
      );
      let competitionIndexNew = -1;
      let eventIndexNew = -1;
      // Quick fix for testing ET markets
      competitionIndexNew = competitionList.findIndex(
        competition =>
          competition.id === payload.competition.id &&
          competition.live === payload["inRunning"] // should be checking if comp is live but api is returning false for payload.competition.live
      );

      if (competitionIndexNew !== -1) {
        eventIndexNew = competitionList[competitionIndexNew].events.findIndex(
          event => event.id === payload.id
        );
      }

      if (eventIndexNew !== -1) {
        // merge the new changes
        const newEvent = Object.assign(
          competitionList[competitionIndexNew].events[eventIndexNew],
          payload
        );
        // ARCH-85 update modal event with latest event data
        if (unsummarizedMarketsModalEventId === newEvent.id)
          unsummarizedMarketsModalEvent = newEvent;
        competitionList[competitionIndexNew].events[eventIndexNew] = newEvent;
      }

      return {
        ...state,
        competitionList,
        unsummarizedMarketsModalEvent,
        unsummarizedMarketsModalEventLoading: false,
      };
    },

    /**
     * payload: EventType
     */
    updateEventInCompetitionInList(
      state: CompetitionListModelType,
      payload: EventType
    ): {
      competitionList: any;
      competitionListOffset: number;
      competitionListPerPage: number;
      hasCompetitionListReachedEnd: boolean;
      isCompetitionListLoading: boolean;
      time: string;
      unsummarizedMarketsModalEvent: EventType;
      unsummarizedMarketsModalEventId: number;
    } {
      let competitionList = cloneDeep(state.competitionList);
      let competitionIndex = -1;
      let competitionIndexes = [];
      competitionList.forEach((competition, index) => {
        if (competition.id === payload.competition.id) {
          competitionIndexes.push(index);
        }
      });

      // if competition index available
      if (competitionIndexes.length > 0) {
        // find event index
        let eventIndex = -1;
        competitionIndexes.forEach(index => {
          eventIndex = competitionList[index].events.findIndex(
            event => event.id === payload.id
          );

          if (eventIndex !== -1) {
            competitionIndex = index;
          }
        });

        if (eventIndex !== -1) {
          // If alternativeMarketPeriods not in payload
          // remove it from event in store before merging new changes
          if (!payload.alternativeMarketPeriods) {
            delete competitionList[competitionIndex].events[eventIndex][
              "alternativeMarketPeriods"
            ];
          }

          // merge the new changes
          const newEvent = Object.assign(
            competitionList[competitionIndex].events[eventIndex],
            payload
          );
          competitionList[competitionIndex].events[eventIndex] = newEvent;
        }
      }

      return { ...state, competitionList };
    },

    /**
     * payload: competitionList
     * using this function until web socket update for favourites available
     */
    updateEventFavouriteInCompetitionInList(
      state: CompetitionListModelType,
      payload: EventType,
      favouritesOnly: boolean
    ): {
      competitionList: any;
      competitionListOffset: number;
      competitionListPerPage: number;
      hasCompetitionListReachedEnd: boolean;
      isCompetitionListLoading: boolean;
      time: string;
      unsummarizedMarketsModalEvent: EventType;
      unsummarizedMarketsModalEventId: number;
    } {
      let competitionList = cloneDeep(state.competitionList);
      const competitionIndex = competitionList.findIndex(
        competition => competition.id === payload.competition.id
      );

      // if competition index available
      if (competitionIndex !== -1) {
        // find event index
        const eventIndex = competitionList[competitionIndex].events.findIndex(
          event => event.id === payload.id
        );

        if (eventIndex !== -1) {
          // merge new favourite status
          if (!payload.favourite && favouritesOnly) {
            // BET-3119 if favourites screen selected remove from list
            competitionList[competitionIndex].events.splice(eventIndex, 1);
            // if last event in competition remove competition from competitionList
            if (competitionList[competitionIndex].events.length === 0)
              competitionList.splice(competitionIndex, 1);
          } else {
            competitionList[competitionIndex].events[eventIndex].favourite =
              payload.favourite;
          }
        }
      }

      return { ...state, competitionList };
    },

    setCompetitionListRequest(state: Record<string, any>): any {
      return {
        ...state,
        isCompetitionListLoading: true,
        setCompetitionListLoading: true,
      };
    },

    setEuroCompetitionListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      // When polling the competition list on the live tab
      // If an event has event details open, need to keep open after updated comp list is fetched
      if (
        !SessionHelper.isAsianRoute() &&
        state.competitionList &&
        payload.competitionList
      ) {
        EventHelper.updateActiveEvents(
          state.competitionList,
          payload.competitionList
        );
      }
      //

      let successObject = {
        ...state,
        competitionList: payload.competitionList,
        competitionListTotal: payload.competitionListTotal,
        // CHECK why there are 2 loading
        setCompetitionListLoading: false,
        isCompetitionListLoading: false,
        //
        // only used in competitionsScreenMobile
        // and can probably be removed from there
        hasCompetitionListReachedEnd: payload.competitionList.length === 0,
        competitionListNumberLiveEvents:
          payload.competitionListNumberLiveEvents,
        fullListLoaded: payload.fullListLoaded || false,

        // Only asian
        unfilteredCompetitionCount: payload.unfilteredCompetitionCount,
        filteredCompetitionCount: payload.filteredCompetitionCount,

        // check if needed to set these here
        // shouldn't be set here
        // but something else might be dependent on it getting set after competitions fetch
        singleCompetitionOnly: false,
        searchResult: false,
        unsummarizedMarketsModalEventLoading: false,
        //
      };

      return successObject;
    },

    /**
     * payload: { offset, competitionList }
     */
    setSingleCompetitionListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      // When polling the competition list on the live tab
      // If an event has event details open, need to keep open after updated comp list is fetched
      if (state.competitionList && payload.competitionList) {
        EventHelper.updateActiveEvents(
          state.competitionList,
          payload.competitionList
        );
      }

      return {
        ...state,
        competitionList: payload.competitionList,
        competitionListTotal: payload.competitionListTotal,
        competitionListOffset: payload.offset + state.competitionListPerPage,
        isCompetitionListLoading: false,
        setCompetitionListLoading: false,
        hasCompetitionListReachedEnd: true,
        competitionListNumberLiveEvents:
          payload.competitionListNumberLiveEvents,
        singleCompetitionOnly: payload.singleCompetitionOnly,
        type: payload.type,
      };
    },

    /**
     * payload: { updateAsianPreGameEventsOnly:boolean }
     */
    updateAsianPreGameEventsOnly: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      return { ...state, asianPreGameEventsOnly: payload };
    },

    /**
     * payload: {hasBetsOnEvent: boolean, eventId: number}
     */
    setHasBetsOnEvent: (
      state: Record<string, any>,
      payload: Record<string, any>
    ): any => {
      // Set hasBetsOnEvents flag so that the position matrix icon will show, immediately after successful bet
      let competitionList = cloneDeep(state.competitionList);
      competitionList.forEach(competition => {
        competition.events.forEach(event => {
          if (event.id === payload.eventId) {
            event.hasBetsOnEvent = true;
          }
        });
      });
      return { ...state, competitionList };
    },

    /**
     * payload: { time: string }
     */
    updateCompetitionListTime: (
      state: Record<string, any>,
      payload: string
    ): any => {
      return { ...state, time: payload };
    },

    /**
     * Set TODAY, EARLY, ALL
     */
    setCompetitionListTime(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return { ...state, time: payload.time };
    },
  },
  effects: dispatch => ({
    async setAsianCompetitionList(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ): Promise<boolean> {
      dispatch.competitionListModel.setCompetitionListRequest();
      const fullList = (payload && payload.fullList) || false;
      const asianParams = AsianCompetitionHelper.getAsianParams(rootState, {
        fullList,
      });
      const favouritesOnly = rootState.competitionListModel.favouritesOnly;
      // The check to stop polling competitions endpoint when favourites are active should be
      // done in the competitions screen
      // Am copying the following check for !favouritesOnly from the previous setCompetionList
      // refactoring how the competitions poll is started and stopped is too big to add to the scope of the
      // params refactor ticket
      return favouritesOnly
        ? // adding fix where old logic expected new polling of competitions list
          dispatch.competitionListModel.resetLoadingOnCloseFavourites()
        : api
            .getCompetitionList(asianParams)
            .then((competitionData: CompetitionDataType) => {
              if (
                competitionData !== undefined &&
                competitionData.content !== undefined
              ) {
                competitionData.competitions = competitionData.content;
                delete competitionData.content;
              }

              if (
                competitionData !== undefined &&
                competitionData.competitions !== undefined
              ) {
                dispatch.competitionListModel.setEuroCompetitionListSuccess({
                  competitionList: competitionData.competitions,
                  competitionListTotal: competitionData.totalElements,
                  competitionListNumberLiveEvents: competitionData.totalLive,
                  fullListLoaded:
                    fullList ||
                    competitionData.totalElements < ListRenderValues.THRESHOLD,
                  unfilteredCompetitionCount:
                    competitionData.unfilteredCompetitionCount,
                  filteredCompetitionCount:
                    competitionData.filteredCompetitionCount,
                });
              }
            })
            .catch(error => {
              throw error;
            });
    },
    async setEuroCompetitionList(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ): Promise<boolean> {
      dispatch.competitionListModel.setCompetitionListRequest();
      const fullList = (payload && payload.fullList) || false;
      const params = CompetitionHelper.getEuroParams(rootState, { fullList });

      if (rootState.competitionListModel.favouritesOnly) {
        // Load favorites view
        const favouriteData = await getFavouriteList({
          showMarketsGroupedByPeriodAndType: true,
          line: payload?.line || "",
        });

        dispatch.competitionListModel.setEuroCompetitionListSuccess({
          competitionList: favouriteData.competitions,
          competitionListTotal: favouriteData.total,
          fullListLoaded: true,
        });

        return true;
      }

      return api
        .getCompetitionList(params)
        .then((competitionData: CompetitionDataType) => {
          if (
            competitionData !== undefined &&
            competitionData.content !== undefined
          ) {
            competitionData.competitions = competitionData.content;
            delete competitionData.content;
          }

          if (
            competitionData !== undefined &&
            competitionData.competitions !== undefined
          ) {
            dispatch.competitionListModel.setEuroCompetitionListSuccess({
              competitionList: competitionData.competitions,
              competitionListTotal: competitionData.totalElements,
              competitionListNumberLiveEvents: competitionData.totalLive,
              fullListLoaded:
                fullList ||
                competitionData.totalElements < ListRenderValues.THRESHOLD,
            });
          }
        })
        .catch(error => {
          throw error;
        });
    },

    /**
     * payload: { }
     */
    async setSingleCompetitionList(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ): Promise<any> {
      this.setCompetitionListRequest();
      const size = rootState.competitionListModel.competitionListPerPage;
      const page = 0;

      return api
        .getSingleCompetitionList(size, page, payload)
        .then((competitionData: CompetitionDataType) => {
          // Pagination for api V3 uses content instead of a specific name, eg competitions in this case.
          // So rename it here back to competitions
          // API are looking into if this springboot behaviour can be changed...
          // Temporally permanent
          // move to helper if permanent permanent
          if (
            competitionData !== undefined &&
            competitionData.content !== undefined
          ) {
            competitionData.competitions = competitionData.content;
            delete competitionData.content;
          }
          //
          if (
            competitionData !== undefined &&
            competitionData.competitions !== undefined
          ) {
            // If activeEventId exists -> Set event with that ID to active so it's expanded on render
            if (
              payload !== undefined &&
              payload.activeEventId &&
              competitionData.competitions[0]
            ) {
              competitionData.competitions[0].events.forEach((event, index) => {
                if (
                  event.id === payload.activeEventId &&
                  event["numberOfMarkets"] > 0
                ) {
                  event.isActive = true;
                }
              });
            }
            this.setSingleCompetitionListSuccess({
              offset: competitionData.offset,
              competitionList: competitionData.competitions,
              competitionListTotal: competitionData.total,
              competitionListNumberLiveEvents: competitionData.totalLive,
              singleCompetitionOnly: true,
              type: TabTypes.ALL,
            });
          }
        })
        .catch(error => {
          throw error;
        });
    },

    /**
     * payload: { }
     */
    async setCompetitionWithFavouritesList(
      payload: Record<string, any>,
      rootState: Record<string, any>
    ): Promise<any> {
      return getFavouriteList(payload)
        .then((competitionData: CompetitionDataType) => {
          if (
            competitionData !== undefined &&
            competitionData.competitions !== undefined
          ) {
            this.setSingleCompetitionListSuccess({
              offset: competitionData.offset,
              competitionList: competitionData.competitions,
              competitionListTotal: competitionData.total,
              singleCompetitionOnly:
                rootState.competitionListModel.singleCompetitionOnly,
            });
          }
        })
        .catch(error => {
          throw error;
        });
    },
  }),
};
export default CompetitionListModel;
